import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { ImportExportConfig, ImportResult, ExportResult, ComparisonResult, ConflictRecord, CompareDataItem, CompareResponse } from '../types/ImportExportTypes';
import { ImportExportEntity, EntityFilter } from '../apiService/importExportApiService';
import ImportExportApiService from '../apiService/importExportApiService';
import DownloadModalMAIQ from 'app/components/DownloadModalMAIQ';
import { isFileSafeToProcess, createMemorySafeProcessor } from '../utils/memoryUtils';
import { parseJsonStringsInData } from '../utils/dataUtils';

// Define the context type
export interface ImportExportContextType {
  // State
  config: ImportExportConfig;
  loading: boolean;
  error: string | null;
  
  // Export state
  exportData: any[] | Blob;
  exportLoading: boolean;
  exportError: string | null;
  exportFilename: string | null;
  
  // Import state
  importFile: File | null;
  importData: any[];
  importLoading: boolean;
  importError: string | null;
  
  // Comparison state
  comparisonData: ComparisonResult | null;
  comparisonLoading: boolean;
  comparisonError: string | null;
  
  // New compare state for ZIP file comparison
  compareData: CompareDataItem[] | null;
  compareLoading: boolean;
  compareError: string | null;
  
  // Validation state
  validationResult: any | null;
  validationLoading: boolean;
  
  // Entity selection state
  availableEntities: ImportExportEntity[];
  selectedEntity: ImportExportEntity | null;
  entityFilters: EntityFilter[];
  entitiesLoading: boolean;
  entitiesError: string | null;
  
  // Download modal state - moved to component level
  
  // Actions
  handleExport: (filters?: Record<string, any>) => Promise<void>;
  handleImport: (file: File, validateOnly?: boolean) => Promise<void>;
  handleCompare: (file: File) => Promise<void>;
  handleCompareZip: (file: File) => Promise<void>;
  handleResolveConflict: (field: string, row: number, value: any) => void;
  handleAcceptChanges: () => Promise<void>;
  handleRejectChanges: () => void;
  handleImportCompareData: () => Promise<void>;
  
  // Entity selection actions
  loadEntities: () => Promise<void>;
  selectEntity: (entity: ImportExportEntity) => void;
  
  // Download modal actions - moved to component level
  
  // Utility functions
  resetState: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setImportError: (error: string | null) => void;
  setImportFile: (file: File | null) => void;
}

// Create the context with default values
const ImportExportContext = createContext<ImportExportContextType>({
  config: {} as ImportExportConfig,
  loading: false,
  error: null,
  exportData: [],
  exportLoading: false,
  exportError: null,
  exportFilename: null,
  importFile: null,
  importData: [],
  importLoading: false,
  importError: null,
  comparisonData: null,
  comparisonLoading: false,
  comparisonError: null,
  compareData: null,
  compareLoading: false,
  compareError: null,
  validationResult: null,
  validationLoading: false,
  availableEntities: [],
  selectedEntity: null,
  entityFilters: [],
  entitiesLoading: false,
  entitiesError: null,
  handleExport: async () => {},
  handleImport: async () => {},
  handleCompare: async () => {},
  handleCompareZip: async () => {},
  handleResolveConflict: () => {},
  handleAcceptChanges: async () => {},
  handleRejectChanges: () => {},
  handleImportCompareData: async () => {},
  loadEntities: async () => {},
  selectEntity: () => {},
  resetState: () => {},
  clearError: () => {},
  setLoading: () => {},
  setError: () => {},
  setImportError: () => {},
  setImportFile: () => {},
});

// Define the provider component
const ImportExportProvider: React.FC<{ 
  children: React.ReactNode; 
  config: ImportExportConfig;
}> = ({ children, config }) => {
  const isMounted = useRef(true);

  // on unmount, mark it false
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Export state
  const [exportData, setExportData] = useState<any[] | Blob>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportFilename, setExportFilename] = useState<string | null>(null);
  
  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  
  // Comparison state
  const [comparisonData, setComparisonData] = useState<ComparisonResult | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);
  
  // New compare state for ZIP file comparison
  const [compareData, setCompareData] = useState<CompareDataItem[] | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  
  // Validation state
  const [validationResult, setValidationResult] = useState<any | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  
  // Entity selection state
  const [availableEntities, setAvailableEntities] = useState<ImportExportEntity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<ImportExportEntity | null>(null);
  const [entityFilters, setEntityFilters] = useState<EntityFilter[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  
  // Download modal state - moved to component level

  // Download modal actions - moved to component level

  // Utility functions
  const clearError = useCallback(() => {
    if (!isMounted.current) return;
    setError(null);
    setExportError(null);
    setImportError(null);
    setComparisonError(null);
    setCompareError(null);
    setEntitiesError(null);
  }, []);

  const resetState = useCallback(() => {
    if (!isMounted.current) return;
    
    // Reset export states
    setExportData([]);
    setExportLoading(false);
    setExportError(null);
    setExportFilename(null);
    
    // Reset import states
    setImportFile(null);
    setImportData([]);
    setImportLoading(false);
    setImportError(null);
    
    // Reset comparison states
    setComparisonData(null);
    setComparisonLoading(false);
    setComparisonError(null);
    
    // Reset new compare states
    setCompareData(null);
    setCompareLoading(false);
    setCompareError(null);
    
    // Reset validation states
    setValidationResult(null);
    setValidationLoading(false);
    
    // Reset entity selection states
    setSelectedEntity(null);
    setEntityFilters([]);
    setEntitiesLoading(false);
    setEntitiesError(null);
    
    // Reset general states
    setLoading(false);
    clearError();
  }, [clearError]);

  // Set import file function
  const setImportFileState = useCallback((file: File | null) => {
    if (!isMounted.current) return;
    setImportFile(file);
  }, []);

  // Export handler
  const handleExport = useCallback(async (filters?: Record<string, any>) => {
    if (!isMounted.current) return;
    
    try {
      setExportLoading(true);
      setExportError(null);
      
      const entityType = filters?.entityType || config.entityType;
      
      // Export will be handled by component-level download modal
      
      const params: any = {
        format: filters?.format || 'zip', // Default format
        ...filters
      };
      
      if (config.dealerId) {
        params.dealer = config.dealerId;
      }
      
      // Handle limit parameter
      if (filters?.limit) {
        params.limit = filters.limit;
      }
      
      // Handle skip parameter
      if (filters?.skip) {
        params.skip = filters.skip;
      }
      
      // Handle fields parameter
      if (filters?.fields) {
        params.fields = filters.fields;
      }
      
      const response = await ImportExportApiService.exportData(entityType, params);
      
      // Check if response is successful
      if (response.statusCode === 200 && response.body) {
        if (!isMounted.current) return;
        
        // Store download data for component to use
        setExportData(response.body);
        
        // Store filename from response if available
        if (response.filename) {
          setExportFilename(response.filename);
        }
        
        // Note: Don't create blob URL here - let the component handle it
        // This prevents timing issues with blob URL creation
      } else {
        throw new Error(response.message || 'Export failed');
      }
    } catch (error: any) {
      if (!isMounted.current) return;
      const errorMessage = error.message || 'Export failed';
      setExportError(errorMessage);
    } finally {
      if (isMounted.current) {
        setExportLoading(false);
      }
    }
  }, [config.entityType, config.dealerId]);

  // Import handler
  const handleImport = useCallback(async (file: File, validateOnly: boolean = false) => {
    if (!isMounted.current) return;
    
    try {
      setImportLoading(true);
      setImportError(null);
      setImportFile(file);
      
      // Check if file is safe to process based on memory estimation
      if (!isFileSafeToProcess(file, 50)) { // 50MB memory limit
        throw new Error('File is too large for safe processing. Please use a smaller file to prevent browser memory issues.');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      const params: any = {
        validate_only: validateOnly,
        update_existing: true
      };
      
      if (config.dealerId) {
        params.dealer = config.dealerId;
      }
      
      // Use selected entity if available, otherwise fallback to config.entityType
      const entityType = selectedEntity?.entity || config.entityType;
      const response = await ImportExportApiService.importData(entityType, formData, params);
      
      if (response.body) {
        if (!isMounted.current) return;
        
        setImportData(response.body);
        setValidationResult(response.body);
      } else {
        throw new Error(response.message || 'Import failed');
      }
    } catch (error: any) {
      if (!isMounted.current) return;
      const errorMessage = error.message || 'Import failed';
      setImportError(errorMessage);
    } finally {
      if (isMounted.current) {
        setImportLoading(false);
      }
    }
  }, [config.entityType, config.dealerId, selectedEntity?.entity]);

  // Compare handler
  const handleCompare = useCallback(async (file: File) => {
    if (!isMounted.current) return;
    
    try {
      setComparisonLoading(true);
      setComparisonError(null);
      
      
      const formData = new FormData();
      formData.append('file', file);
      
      const params: any = {};
      if (config.dealerId) {
        params.dealer = config.dealerId;
      }
      
      // Use selected entity if available, otherwise fallback to config.entityType
      const entityType = selectedEntity?.entity || config.entityType;
      const response = await ImportExportApiService.compareEntities(entityType, formData, params);
      
      if (response.body) {
        if (!isMounted.current) return;
        
        const comparisonResult: ComparisonResult = {
          newRecords: response.body.new_entities || [],
          existingRecords: response.body.existing_entities || [],
          conflicts: response.body.conflicts || [],
          summary: response.body.summary || {
            totalNew: 0,
            totalExisting: 0,
            totalConflicts: 0
          }
        };
        
        setComparisonData(comparisonResult);
      } else {
        throw new Error(response.message || 'Comparison failed');
      }
    } catch (error: any) {
      if (!isMounted.current) return;
      const errorMessage = error.message || 'Comparison failed';
      setComparisonError(errorMessage);
    } finally {
      if (isMounted.current) {
        setComparisonLoading(false);
      }
    }
  }, [config.entityType, config.dealerId, selectedEntity?.entity]);

  // Compare handler - works for all file types (ZIP, CSV, etc.)
  const handleCompareZip = useCallback(async (file: File) => {
    if (!isMounted.current) return;
    
    try {
      setCompareLoading(true);
      setCompareError(null);
      
      // Check if file is safe to process based on memory estimation
      if (!isFileSafeToProcess(file, 50)) { // 50MB memory limit
        throw new Error('File is too large for safe processing. Please use a smaller file to prevent browser memory issues.');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      const params: any = {};
      if (config.dealerId) {
        params.dealer = config.dealerId;
      }
      
      // Use selected entity if available, otherwise fallback to config.entityType
      const entityType = selectedEntity?.entity || config.entityType;
      const response = await ImportExportApiService.compareEntities(entityType, formData, params);
      
      if (response.body && Array.isArray(response.body)) {
        if (!isMounted.current) return;
        
        // Parse JSON strings in the data (for CSV imports where objects/arrays come as strings)
        const parsedData = parseJsonStringsInData(response.body);
        
        setCompareData(parsedData);
      } else {
        throw new Error(response.message || 'File comparison failed');
      }
    } catch (error: any) {
      if (!isMounted.current) return;
      const errorMessage = error.message || 'File comparison failed';
      setCompareError(errorMessage);
    } finally {
      if (isMounted.current) {
        setCompareLoading(false);
      }
    }
  }, [config.entityType, config.dealerId, selectedEntity?.entity]);

  // Conflict resolution handler
  const handleResolveConflict = useCallback((field: string, row: number, value: any) => {
    if (!isMounted.current || !comparisonData) return;
    
    setComparisonData(prev => {
      if (!prev) return prev;
      
      const updatedConflicts = prev.conflicts.map((conflict: ConflictRecord) => {
        if (conflict.field === field && conflict.row === row) {
          return {
            ...conflict,
            resolvedValue: value,
            resolved: true
          };
        }
        return conflict;
      });
      
      return {
        ...prev,
        conflicts: updatedConflicts
      };
    });
  }, [comparisonData]);

  // Accept changes handler
  const handleAcceptChanges = useCallback(async () => {
    if (!isMounted.current || !comparisonData) return;
    
    try {
      setLoading(true);
      
      
      // Process resolved conflicts and new records
      const processedData = [...comparisonData.newRecords];
      
      // Add resolved conflicts to existing records
      comparisonData.conflicts.forEach((conflict: any) => {
        if (conflict.resolved && conflict.resolvedValue !== undefined) {
          const existingRecord = comparisonData.existingRecords[conflict.row];
          if (existingRecord) {
            existingRecord[conflict.field] = conflict.resolvedValue;
          }
        }
      });
      
      processedData.push(...comparisonData.existingRecords);
      
      // Import the processed data
      // Use selected entity if available, otherwise fallback to config.entityType
      const entityType = selectedEntity?.entity || config.entityType;
      const response = await ImportExportApiService.importDataFromJson(entityType, processedData, {
        dealer: config.dealerId,
        validate_only: false,
        update_existing: true
      });
      
      if (response.body) {
        if (!isMounted.current) return;
        
        resetState();
      } else {
        throw new Error(response.message || 'Failed to accept changes');
      }
    } catch (error: any) {
      if (!isMounted.current) return;
      const errorMessage = error.message || 'Failed to accept changes';
      setError(errorMessage);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [comparisonData, config.entityType, config.dealerId, resetState, selectedEntity?.entity]);

  // Import compare data handler - imports the incoming data from compare results
  const handleImportCompareData = useCallback(async () => {
    if (!isMounted.current || !compareData) return;
    
    try {
      setLoading(true);
      
      // Extract incoming data from compare results
      const incomingData = compareData
        .filter(item => item.incoming) // Only items with incoming data
        .map(item => item.incoming); // Extract the incoming data
      
      if (incomingData.length === 0) {
        throw new Error('No data to import');
      }
      
      
      // Import the incoming data
      // Use selected entity if available, otherwise fallback to config.entityType
      const entityType = selectedEntity?.entity || config.entityType;
      const response = await ImportExportApiService.importDataFromJson(entityType, incomingData, {
        dealer: config.dealerId,
        validate_only: false,
        update_existing: true
      });
      
      if (response.body) {
        if (!isMounted.current) return;
        
        resetState();
      } else {
        throw new Error(response.message || 'Failed to import data');
      }
    } catch (error: any) {
      if (!isMounted.current) return;
      const errorMessage = error.message || 'Failed to import data';
      setError(errorMessage);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [compareData, config.entityType, config.dealerId, resetState, selectedEntity?.entity]);

  // Reject changes handler
  const handleRejectChanges = useCallback(() => {
    if (!isMounted.current) return;
    
    resetState();
  }, [config.entityType, resetState]);

  // Load entities handler
  const loadEntities = useCallback(async () => {
    if (!isMounted.current) return;
    
    try {
      setEntitiesLoading(true);
      setEntitiesError(null);
      
      
      const response = await ImportExportApiService.getEntities();
      
      if (response.body && Array.isArray(response.body)) {
        if (!isMounted.current) return;
        
        setAvailableEntities(response.body);
      } else {
        throw new Error(response.message || 'Failed to load entities');
      }
    } catch (error: any) {
      if (!isMounted.current) return;
      const errorMessage = error.message || 'Failed to load entities';
      setEntitiesError(errorMessage);
    } finally {
      if (isMounted.current) {
        setEntitiesLoading(false);
      }
    }
  }, []);

  // Select entity handler
  const selectEntity = useCallback((entity: ImportExportEntity) => {
    if (!isMounted.current) return;
    
    setSelectedEntity(entity);
    setEntityFilters(entity.filters || []);
  }, []);

  // Provide the context value
  const contextValue: ImportExportContextType = {
    config,
    loading,
    error,
    exportData,
    exportLoading,
    exportError,
    exportFilename,
    importFile,
    importData,
    importLoading,
    importError,
    comparisonData,
    comparisonLoading,
    comparisonError,
    compareData,
    compareLoading,
    compareError,
    validationResult,
    validationLoading,
    availableEntities,
    selectedEntity,
    entityFilters,
    entitiesLoading,
    entitiesError,
    handleExport,
    handleImport,
    handleCompare,
    handleCompareZip,
    handleResolveConflict,
    handleAcceptChanges,
    handleRejectChanges,
    handleImportCompareData,
    loadEntities,
    selectEntity,
    resetState,
    clearError,
    setLoading,
    setError,
    setImportError,
    setImportFile: setImportFileState,
  };

  return (
    <ImportExportContext.Provider value={contextValue}>
      {children}
      {/* DownloadModalMAIQ is now handled at component level */}
    </ImportExportContext.Provider>
  );
};

// Custom hook to use the context
export const useImportExport = () => {
  const context = useContext(ImportExportContext);
  if (!context) {
    throw new Error('useImportExport must be used within an ImportExportProvider');
  }
  return context;
};

export { ImportExportContext };
export default ImportExportProvider;
